import { DailyNavOverrideForm } from 'api/models/DailyNavOverrideForm';
import { DailyNavOverrideSecurity } from 'api/models/DailyNavOverrideSecurity';
import { DailyNavPriceOverrideRequest } from 'api/models/DailyNavPriceOverrideRequest';
import { DailyNavRecord } from 'api/models/DailyNavRecord';
import { OverridePriceCurrencyMode, OverridePriceValues } from './OverridePriceDialog.props';

// Enough to round-trip a typed price through the rate; the API accepts 15.
const CONVERTED_DECIMALS = 10;
// What the input shows: stored prices can carry float noise (425.48223310000003).
const DISPLAY_DECIMALS = 6;
const PRICE_PATTERN = /^\d*\.?\d+$/;

// The server's own gate (the same check the override POST runs), not a copy of it.
export const isOverrideAvailable = (record?: DailyNavRecord | null): boolean => Boolean(record?.override_available);

const trimDecimal = (value: string): string =>
  value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;

const fxRate = (form: DailyNavOverrideForm): number | null => {
  const rate = Number(form.currency.fx_rate);
  return form.currency.fx_rate != null && Number.isFinite(rate) && rate > 0 ? rate : null;
};

// The company's cap table currency differs from the Daily NAV reporting currency.
export const hasCurrencyDifference = (form: DailyNavOverrideForm): boolean =>
  Boolean(form.currency.reporting_currency) &&
  Boolean(form.currency.cap_table_currency) &&
  form.currency.reporting_currency !== form.currency.cap_table_currency;

// Reporting currency is selectable only when it differs and a rate exists.
export const canEnterInReporting = (form: DailyNavOverrideForm): boolean =>
  Boolean(form.currency.reporting_currency) &&
  form.currency.reporting_currency !== form.currency.cap_table_currency &&
  fxRate(form) != null;

export const currencyCode = (form: DailyNavOverrideForm, mode: OverridePriceCurrencyMode): string =>
  (mode === 'reporting' ? form.currency.reporting_currency : form.currency.cap_table_currency) ?? 'USD';

export const otherMode = (mode: OverridePriceCurrencyMode): OverridePriceCurrencyMode =>
  mode === 'reporting' ? 'cap_table' : 'reporting';

const toMode = (capTablePrice: number, form: DailyNavOverrideForm, mode: OverridePriceCurrencyMode): number =>
  mode === 'reporting' ? capTablePrice * (fxRate(form) ?? 1) : capTablePrice;

const formatInput = (value: number): string => trimDecimal(value.toFixed(CONVERTED_DECIMALS));

// The closing price as the input shows it; blank when the security was never priced.
// Rounded for display only: an untouched row posts the exact stored price.
export const prefillValue = (
  security: DailyNavOverrideSecurity,
  form: DailyNavOverrideForm,
  mode: OverridePriceCurrencyMode
): string => {
  if (security.cap_table_price == null) {
    return '';
  }
  return trimDecimal(toMode(Number(security.cap_table_price), form, mode).toFixed(DISPLAY_DECIMALS));
};

export const prefillValues = (form: DailyNavOverrideForm, mode: OverridePriceCurrencyMode): OverridePriceValues =>
  Object.fromEntries(form.securities.map(security => [security.security_id, prefillValue(security, form, mode)]));

export const isValidPrice = (value: string): boolean => PRICE_PATTERN.test(value.trim());

// Grid rows (the common, held securities) can't be 0; the backend refuses it too.
export const isZeroGridPrice = (security: DailyNavOverrideSecurity, value: string): boolean =>
  (security.is_common || security.is_held) && isValidPrice(value) && Number(value) === 0;

export const isEdited = (
  security: DailyNavOverrideSecurity,
  values: OverridePriceValues,
  form: DailyNavOverrideForm,
  mode: OverridePriceCurrencyMode
): boolean => (values[security.security_id] ?? '') !== prefillValue(security, form, mode);

// Untouched rows follow the toggle exactly; edited ones are converted at the rate.
export const switchCurrency = (
  values: OverridePriceValues,
  form: DailyNavOverrideForm,
  from: OverridePriceCurrencyMode
): OverridePriceValues => {
  const to = otherMode(from);
  const rate = fxRate(form) ?? 1;
  return Object.fromEntries(
    form.securities.map(security => {
      const id = security.security_id;
      const current = values[id] ?? '';
      if (!isEdited(security, values, form, from)) {
        return [id, prefillValue(security, form, to)];
      }
      if (!isValidPrice(current)) {
        return [id, current];
      }
      const amount = Number(current);
      return [id, formatInput(to === 'reporting' ? amount * rate : amount / rate)];
    })
  );
};

// The other currency's figure for the hint column; null when it cannot be shown.
export const counterpartAmount = (
  value: string,
  form: DailyNavOverrideForm,
  mode: OverridePriceCurrencyMode
): number | null => {
  const rate = fxRate(form);
  if (rate == null || !isValidPrice(value)) {
    return null;
  }
  return mode === 'reporting' ? Number(value) / rate : Number(value) * rate;
};

// Always posted in the cap table currency: an untouched row sends its exact
// closing price, so "keeps its current price" holds to the last decimal.
export const buildOverrideRequest = (
  form: DailyNavOverrideForm,
  values: OverridePriceValues,
  mode: OverridePriceCurrencyMode,
  note: string
): DailyNavPriceOverrideRequest => {
  const rate = fxRate(form) ?? 1;
  return {
    // The allocation the prefill came from; refused if Today's Price has moved since.
    source_allocation_id: form.closing_allocation_id,
    currency: form.currency.cap_table_currency ?? '',
    note: note.trim(),
    share_prices: form.securities.map(security => {
      const value = (values[security.security_id] ?? '').trim();
      let price = value;
      if (!isEdited(security, values, form, mode) && security.cap_table_price != null) {
        price = security.cap_table_price;
      } else if (mode === 'reporting') {
        price = formatInput(Number(value) / rate);
      }
      return { security_id: security.security_id, price_per_share: price };
    }),
  };
};

export const formatAmount = (value: number | null | undefined, code: string): string =>
  value == null
    ? '—'
    : value.toLocaleString('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
